/**
 * @architecture-header 1
 * @layer shared
 * @domain visual-language
 * @responsibility Render the status glyphs and status text pairing.
 * @state-owner none
 * @allowed-imports React
 * @forbidden business rules; network; store access
 * @public-api workspace modules
 * @side-effects none
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import type { ReactNode } from 'react';

export type StatusGlyph = 'done' | 'live' | 'half' | 'todo' | 'warn' | 'err' | 'fix' | 'low';

export function StatusIcon({ glyph }: { glyph: StatusGlyph }) {
  switch (glyph) {
    case 'done':
      return (
        <svg aria-hidden="true" className="vsi ok" viewBox="0 0 14 14">
          <circle cx="7" cy="7" r="5.6" />
          <path className="k" d="M4.5 7.2l1.8 1.8 3.3-3.5" />
        </svg>
      );
    case 'live':
      return (
        <svg aria-hidden="true" className="vsi pr" viewBox="0 0 14 14">
          <circle cx="7" cy="7" r="5.6" />
          <path className="f" d="M7 7V1.4A5.6 5.6 0 0 1 12.6 7z" />
        </svg>
      );
    case 'half':
      return (
        <svg aria-hidden="true" className="vsi pr" viewBox="0 0 14 14">
          <circle cx="7" cy="7" r="5.6" />
          <path className="f" d="M7 7V1.4A5.6 5.6 0 0 1 7 12.6z" />
        </svg>
      );
    case 'todo':
      return (
        <svg aria-hidden="true" className="vsi" viewBox="0 0 14 14">
          <circle cx="7" cy="7" r="5.6" strokeDasharray="2 2.2" />
        </svg>
      );
    case 'warn':
      return (
        <svg aria-hidden="true" className="vsi wn" viewBox="0 0 14 14">
          <circle cx="7" cy="7" r="5.6" />
          <path className="k" d="M7 4.2v3.3" />
          <circle className="d" cx="7" cy="9.9" r=".9" />
        </svg>
      );
    case 'fix':
      return (
        <svg aria-hidden="true" className="vsi vfx" viewBox="0 0 14 14">
          <circle cx="7" cy="7" r="5.6" />
          <path className="k" d="M4.5 7.2l1.8 1.8 3.3-3.5" />
        </svg>
      );
    case 'low':
      return (
        <svg aria-hidden="true" className="vsi vlo" viewBox="0 0 14 14">
          <path d="M7 1.6l5.7 10.2H1.3z" />
          <path className="k" d="M7 5.6v2.9" />
          <circle className="d" cx="7" cy="10.4" r=".8" />
        </svg>
      );
    default:
      return (
        <svg aria-hidden="true" className="vsi er" viewBox="0 0 14 14">
          <circle cx="7" cy="7" r="5.6" />
          <path className="k" d="M7 4.2v3.3" />
          <circle className="d" cx="7" cy="9.9" r=".9" />
        </svg>
      );
  }
}

export type StatusTone = '' | 'hot' | 'wn' | 'pr';

export function StatusText({
  glyph,
  label,
  tone = '',
}: {
  glyph: StatusGlyph;
  label: ReactNode;
  tone?: StatusTone;
}) {
  return (
    <span className={`vstx ${tone}`}>
      <StatusIcon glyph={glyph} />
      {label}
    </span>
  );
}

export function ProgressBars({ level }: { level: number }) {
  return (
    <span className="vbars">
      {[0, 1, 2].map((index) => (
        <i className={index < level ? 'on' : ''} key={index} />
      ))}
    </span>
  );
}

export function NameCell({
  icon,
  text,
  tone = '',
}: {
  icon: ReactNode;
  text: string;
  tone?: '' | 'p' | 'e';
}) {
  return (
    <span className="vnmc">
      <span className={`vnic ${tone}`}>{icon}</span>
      <b>{text}</b>
    </span>
  );
}
